'use client';

import React, { useState } from 'react';

const testimonials = [
    {
        quote: "I tested 3 ideas in a week. The third one got 200 signups in 2 days. That's the one I'm building now. Saved me months of guessing.",
        author: "Rahul Sharma",
        role: "Indie Hacker",
        avatar: "R",
    },
    {
        quote: "Finally, a tool that gets what early-stage founders need. Not a bloated page builder—just a quick way to test if anyone cares about your idea.",
        author: "Priya Patel",
        role: "First-time Founder",
        avatar: "P",
    },
    {
        quote: "16% conversion on my landing page told me everything I needed to know. Started building the MVP the next day with confidence.",
        author: "Arjun Mehta",
        role: "Solo Developer",
        avatar: "A",
    },
    {
        quote: "The AI-generated copy was surprisingly good. I tweaked maybe 2 sentences. Had my page live in 10 minutes flat.",
        author: "Sneha Gupta",
        role: "Product Designer turned Founder",
        avatar: "S",
    },
    {
        quote: "Used it to validate a B2B SaaS idea. 47 signups from cold LinkedIn outreach. Now I have 47 people to interview before building.",
        author: "Vikram Singh",
        role: "Ex-Consultant, Now Founder",
        avatar: "V",
    },
    {
        quote: "The dashboard showing real-time signups is addictive. But more importantly, the conversion rate helped me decide which idea to pursue.",
        author: "Neha Reddy",
        role: "Serial Side-Projector",
        avatar: "N",
    },
];

export default function TestimonialsSection() {
    const [isPaused, setIsPaused] = useState(false);

    // Duplicate testimonials for seamless infinite scroll
    const duplicatedTestimonials = [...testimonials, ...testimonials];

    return (
        <section className="py-16 sm:py-24 bg-white overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="text-center mb-12 sm:mb-16">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-gray-900">
                        Don't Just Take Our Word for It
                    </h2>
                    <p className="mt-4 sm:mt-6 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
                        Founders who validated before they built
                    </p>
                </div>
            </div>

            {/* Carousel Container - Full width */}
            <div
                className="relative w-full"
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
                onTouchStart={() => setIsPaused(true)}
                onTouchEnd={() => setIsPaused(false)}
            >
                {/* Gradient overlays for smooth edges */}
                <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
                <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

                {/* Scrolling track */}
                <div
                    className={`flex gap-4 sm:gap-6 ${isPaused ? 'testimonial-paused' : 'testimonial-scroll'}`}
                    style={{
                        width: 'max-content',
                    }}
                >
                    {duplicatedTestimonials.map((testimonial, index) => (
                        <TestimonialCard key={index} {...testimonial} />
                    ))}
                </div>
            </div>

            {/* CSS for animation */}
            <style jsx>{`
                @keyframes scroll {
                    0% {
                        transform: translateX(0);
                    }
                    100% {
                        transform: translateX(-50%);
                    }
                }
                
                .testimonial-scroll {
                    animation: scroll 40s linear infinite;
                }
                
                .testimonial-paused {
                    animation: scroll 40s linear infinite;
                    animation-play-state: paused;
                }
            `}</style>
        </section>
    );
}

interface TestimonialCardProps {
    quote: string;
    author: string;
    role: string;
    avatar: string;
}

function TestimonialCard({ quote, author, role, avatar }: TestimonialCardProps) {
    return (
        <div className="bg-purple-100 rounded-2xl p-5 sm:p-6 border border-purple-200 hover:border-violet-400 transition-all duration-300 hover:-translate-y-1 flex-shrink-0 w-[280px] sm:w-[320px] md:w-[360px]">
            {/* Quote mark */}
            <div className="text-violet-500 mb-3 sm:mb-4">
                <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
            </div>

            {/* Quote */}
            <p className="text-gray-900 text-base sm:text-lg leading-relaxed mb-4 sm:mb-6 line-clamp-4">
                {quote}
            </p>

            {/* Author */}
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-violet-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm sm:text-base">{avatar}</span>
                </div>
                <div>
                    <div className="font-semibold text-gray-900 text-sm sm:text-base">{author}</div>
                    <div className="text-xs sm:text-sm text-gray-500">{role}</div>
                </div>
            </div>
        </div>
    );
}
